import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import purchaseReportService from '../../../../services/purchaseReportService';
import PriceTrackingShell from './PriceTrackingShell';
import usePriceTrackingFilters from './usePriceTrackingFilters';
import {
  formatMoney,
  formatPrice,
  formatPct,
  purchaseLineKey,
  canOpenPurchaseProductDetail,
} from './shared';

export default function PriceTrackingProductsPage() {
  const navigate = useNavigate();
  const { filters, updateFilter, buildParams, suppliers, products } = usePriceTrackingFilters();
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [toast, setToast] = useState({ isOpen: false, message: '', type: 'info' });

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await purchaseReportService.getPriceTrackingDashboard(
        buildParams({ allPurchasedItems: true }),
      );
      if (res.success) {
        setRows(res.data?.top_products || []);
      } else {
        setToast({ isOpen: true, message: res.message || 'Error al cargar', type: 'error' });
      }
    } catch (error) {
      setToast({ isOpen: true, message: 'Error al cargar productos', type: 'error' });
    } finally {
      setLoading(false);
    }
  }, [buildParams]);

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleRowClick = (row) => {
    if (!canOpenPurchaseProductDetail(row)) return;
    navigate(`/compras/reports/price-tracking/products/${row.product_id ?? row.item_id}`);
  };

  return (
    <PriceTrackingShell
      title="Productos comprados"
      subtitle="Todo lo registrado en documentos de compra del período: productos y servicios, ordenado por gasto."
      filters={filters}
      updateFilter={updateFilter}
      suppliers={suppliers}
      products={products}
      onQuery={loadData}
      loading={loading}
      toast={toast}
      onToastClose={() => setToast({ ...toast, isOpen: false })}
      hideInsumosOnlyFilter
    >
      <div className="bg-white rounded-xl shadow-sm overflow-hidden border border-gray-200">
        <div className="px-5 py-4 border-b">
          <h2 className="font-semibold text-gray-800">Ítems comprados por gasto</h2>
          <p className="text-sm text-gray-500 mt-1">
            Solo aparece lo que tiene al menos una línea en facturas u otros documentos de compra.
          </p>
        </div>
        {loading ? (
          <p className="p-8 text-center text-gray-500">Cargando...</p>
        ) : rows.length === 0 ? (
          <p className="p-8 text-center text-gray-500">Sin compras en el período.</p>
        ) : (
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs text-gray-500 uppercase">Ítem</th>
                <th className="px-4 py-3 text-right text-xs text-gray-500 uppercase">Gasto</th>
                <th className="px-4 py-3 text-right text-xs text-gray-500 uppercase">Compras</th>
                <th className="px-4 py-3 text-right text-xs text-gray-500 uppercase">Mín</th>
                <th className="px-4 py-3 text-right text-xs text-gray-500 uppercase">Prom</th>
                <th className="px-4 py-3 text-right text-xs text-gray-500 uppercase">Máx</th>
                <th className="px-4 py-3 text-right text-xs text-gray-500 uppercase">Último</th>
                <th className="px-4 py-3 text-right text-xs text-gray-500 uppercase">Δ%</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {rows.map((row) => {
                const clickable = canOpenPurchaseProductDetail(row);
                return (
                  <tr
                    key={purchaseLineKey(row)}
                    className={clickable ? 'hover:bg-teal-50/40 cursor-pointer' : 'hover:bg-gray-50'}
                    onClick={() => handleRowClick(row)}
                  >
                    <td className="px-4 py-3">
                      <div className="font-medium">
                        {row.product_name}
                        {row.item_type === 'service' ? (
                          <span className="ml-2 text-xs bg-sky-100 text-sky-700 px-1.5 py-0.5 rounded">Servicio</span>
                        ) : row.is_insumo ? (
                          <span className="ml-2 text-xs bg-violet-100 text-violet-700 px-1.5 py-0.5 rounded">Insumo</span>
                        ) : (
                          <span className="ml-2 text-xs bg-gray-100 text-gray-600 px-1.5 py-0.5 rounded">Producto</span>
                        )}
                      </div>
                      <div className="text-xs text-gray-500">
                        {row.sku || '—'} · {row.supplier_count} prov.
                      </div>
                    </td>
                    <td className="px-4 py-3 text-right">{formatMoney(row.total_spend, row.currency)}</td>
                    <td className="px-4 py-3 text-right">{row.purchase_count}</td>
                    <td className="px-4 py-3 text-right text-gray-600">{formatPrice(row.min_price)}</td>
                    <td className="px-4 py-3 text-right">{formatPrice(row.avg_price)}</td>
                    <td className="px-4 py-3 text-right text-gray-600">{formatPrice(row.max_price)}</td>
                    <td className="px-4 py-3 text-right font-medium">{formatPrice(row.last_price)}</td>
                    <td className="px-4 py-3 text-right">
                      <span className={row.variation_pct >= 0 ? 'text-red-600' : 'text-green-600'}>
                        {formatPct(row.variation_pct)}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </PriceTrackingShell>
  );
}
